<template>
  <div id="SectionYourRoadmap">
    <UIContainer class="SectionYourRoadmap__container">
      <SectionTitle class="SectionReviews__title">
        <template #title> Your roadmap from ambition to action </template>
        <template #description>
          We translate your net zero goal into a science-backed, board-ready
          transition plan. Our process is designed to identify the most
          impactful actions that you can actually fund and deliver.
        </template>
      </SectionTitle>

      <div class="SectionYourRoadmap__carousel">
        <UICarousel :items="steps">
          <template #card="{ item }">
            <RoadmapCard
              :index="(item as Step).index"
              :title="(item as Step).title"
              :description="(item as Step).description"
              :image="(item as Step).image"
              :backgroundColor="(item as Step).backgroundColor"
            />
          </template>
        </UICarousel>
      </div>
    </UIContainer>
  </div>
</template>

<script setup lang="ts">
import SectionTitle from '@/views/components/misc/SectionTitle.vue'
import UIContainer from '@/views/components/ui/containers/UIContainer/UIContainer.vue'
import { ref } from 'vue'
import imgBaseline from '../assets/yourRoadmap/baseline.webp'
import imgLevers from '../assets/yourRoadmap/levers.webp'
import imgImpact from '../assets/yourRoadmap/impact.webp'
import imgStrategy from '../assets/yourRoadmap/strategy.webp'
import imgSbti from '../assets/yourRoadmap/sbti.webp'
import imgTimeline from '../assets/yourRoadmap/timeline.webp'
import UICarousel from '@/views/components/UICarousel.vue'
import RoadmapCard from '@/views/components/cards/RoadmapCard.vue'

type Step = {
  index: number
  title: string
  description: string
  image: string
  backgroundColor?: string
}

const steps = ref<Step[]>([
  {
    index: 1,
    title: 'Baseline & forecast',
    description:
      'We start by creating a complete, audit-ready GHG emissions baseline. We then forecast your future emissions to understand your trajectory and set the stage for your SBTi-aligned goals.',
    image: imgBaseline,
    backgroundColor: '#FFFFFF',
  },
  {
    index: 2,
    title: 'Identify decarbonization levers',
    description:
      'We analyze your entire value chain to identify and prioritize the most effective decarbonization opportunities.',
    image: imgLevers,
    backgroundColor: '#F9F9FB',
  },
  {
    index: 3,
    title: 'Model financial impact',
    description:
      'We quantify the financial impacts, risks, and ROI of your decarbonization levers across the short, medium, and long term, building a powerful business case for investment.',
    image: imgImpact,
    backgroundColor: '#EFF1F5',
  },
  {
    index: 4,
    title: 'Build your strategy',
    description:
      'We develop a comprehensive strategy that includes your investment plan, financing options, and a credible approach to carbon offsetting and removal for any residual emissions.',
    image: imgStrategy,
    backgroundColor: '#DCDFEA',
  },
  {
    index: 5,
    title: 'Get SBTi verified',
    description:
      'We provide full, hands-on support with your SBTi application and guide you through the entire verification process, ensuring your targets are approved without costly delays.',
    image: imgSbti,
    backgroundColor: '#EFF1F5',
  },
  {
    index: 6,
    title: 'Note on timeline',
    description:
      'Typical projects run 3-8 weeks for SMEs and 6-12 weeks for large companies, depending on your data readiness. We deliver an interim target readout early, so you’re not waiting to act.',
    image: imgTimeline,
    backgroundColor: '#F9F9FB',
  },
])
</script>

<style lang="scss" scoped>
#SectionYourRoadmap {
  position: relative;
  background-color: $color-white;
  padding: 5rem 0;

  .SectionYourRoadmap__container {
    padding-top: 0;
    padding-bottom: 0;
  }

  .SectionReviews__title {
    max-width: 48rem;
  }

  .SectionYourRoadmap__carousel {
    margin-top: 2rem;
  }
}
</style>
